import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import { HttpStatusCode } from '@capital-access/common/http';
import { LocalizationService } from '@capital-access/common/localization';
import { SystemSettingsService } from '@capital-access/common/settings';
import {
  ContactSupportAdvisor,
  ImpersonationAuthorizationDetailsDto,
  ImpersonationRepository,
  UserRepository
} from '@capital-access/common/user';
import { LoadStatus } from '@capital-access/common/utils';
import {
  DrawerPosition,
  DrawerType,
  FireflyDrawerContent,
  FireflyDrawerService,
  LoadingType,
  NotificationService,
  NotificationType
} from '@capital-access/firefly/components';
import { exportWithdrawList } from './+state/contact-support-drawer.actions';
import { AuthorizeImpersonationComponent } from './authorize-impersonation.component';
import { AuthorizeImpersonationReadonlyComponent } from './authorize-impersonation-readonly.component';
import { DisclaimerComponent } from './disclaimer.component';

@Component({
  templateUrl: './contact-support.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ContactSupportComponent extends FireflyDrawerContent<void> implements OnInit {
  settings = this.settingsService.getSettings();
  loadCsAdvisorStatus$ = new BehaviorSubject(LoadStatus.Pristine);
  csAdvisorData$!: Observable<ContactSupportAdvisor | null>;
  loadStatus = LoadStatus;
  loadAuthImpersonationStatus$ = new BehaviorSubject(LoadStatus.Pristine);
  readOnlyMode = false;
  readonly localizationScope = 'navigation';

  constructor(
    private drawerService: FireflyDrawerService,
    private localization: LocalizationService,
    private settingsService: SystemSettingsService,
    private userRepository: UserRepository,
    private impersonationRepository: ImpersonationRepository,
    private notificationService: NotificationService,

    private store: Store
  ) {
    super();
  }

  get currentYear() {
    return new Date().getFullYear();
  }

  ngOnInit() {
    this.csAdvisorData$ = this.store.select(getFeatureToggle('cs-info-CPD-1797')).pipe(
      switchMap(allowed => {
        if (!allowed) return of(null);
        requestAnimationFrame(() => this.loadCsAdvisorStatus$.next(LoadStatus.Loading));
        return this.userRepository.getUserContactSupportAdvisor().pipe(
          tap(() => this.loadCsAdvisorStatus$.next(LoadStatus.Loaded)),
          catchError(error => {
            //no advisor data found for this user
            if (error && error instanceof HttpErrorResponse && error.status === HttpStatusCode.NOT_FOUND) {
              this.loadCsAdvisorStatus$.next(LoadStatus.Loaded);
            } else {
              this.loadCsAdvisorStatus$.next(LoadStatus.Failed);
            }
            return of(null);
          })
        );
      })
    );
  }

  showDisclaimer() {
    this.localization
      .getLocalization('navigation')
      .pipe(
        map(({ disclaimer }) => {
          return this.drawerService.openDrawer(
            {
              title: disclaimer,
              type: DrawerType.Form,
              position: DrawerPosition.Right,
              addBackButton: true
            },
            DisclaimerComponent
          );
        }),
        take(1)
      )
      .subscribe();
  }

  exportContactsWithdrawList() {
    this.store.dispatch(exportWithdrawList());
  }

  showAuthorizeImpersonation() {
    this.drawerService.setDrawerLoadingState(LoadingType.Overlay);
    if (this.loadAuthImpersonationStatus$.value === LoadStatus.Loading) {
      return;
    }
    requestAnimationFrame(() => this.loadAuthImpersonationStatus$.next(LoadStatus.Loading));
    this.impersonationRepository
      .getImpersonationDetails()
      .pipe(
        take(1),
        tap(response => {
          this.drawerService.setDrawerLoadingState();
          this.readOnlyMode = !!response;
          this.loadAuthImpersonationStatus$.next(LoadStatus.Loaded);

          if (this.readOnlyMode && response) {
            this.openImpersonationReadonlyDrawer(response);
          } else {
            this.openImpersonationEditDrawer();
          }
        }),
        catchError(error => {
          this.drawerService.setDrawerLoadingState();
          this.readOnlyMode = false;
          const isNotFound = error instanceof HttpErrorResponse && error.status === HttpStatusCode.NOT_FOUND;
          this.loadAuthImpersonationStatus$.next(isNotFound ? LoadStatus.Loaded : LoadStatus.Failed);

          if (isNotFound) {
            this.openImpersonationEditDrawer();
          } else {
            this.showNotification('somethingWentWrong', 'danger');
          }

          return of(null);
        })
      )
      .subscribe();
  }

  openImpersonationEditDrawer() {
    this.localization
      .getLocalization('navigation')
      .pipe(
        map(({ authorizeImpersonation }) => {
          return this.drawerService.openDrawer(
            {
              title: authorizeImpersonation,
              type: DrawerType.Form,
              position: DrawerPosition.Right,
              addBackButton: true
            },
            AuthorizeImpersonationComponent
          );
        }),
        take(1)
      )
      .subscribe();
  }

  openImpersonationReadonlyDrawer(authorizationData: ImpersonationAuthorizationDetailsDto) {
    this.localization
      .getLocalization('navigation')
      .pipe(
        map(({ authorizeImpersonation }) => {
          return this.drawerService.openDrawer(
            {
              title: authorizeImpersonation,
              type: DrawerType.Form,
              position: DrawerPosition.Right,
              addBackButton: true
            },
            AuthorizeImpersonationReadonlyComponent,
            authorizationData
          );
        }),
        take(1)
      )
      .subscribe();
  }

  private showNotification(localizationKey: string, type: NotificationType): void {
    this.localization
      .localize(localizationKey, undefined, { scope: this.localizationScope })
      .pipe(take(1))
      .subscribe(msg => {
        this.drawerService.closeDrawersWithoutConfirmation();
        this.notificationService.notify(msg, { type });
      });
  }
}
